import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from 'src/components/atoms/button'
import Dropdown from 'src/components/atoms/dropdown'
import IconButton from 'src/components/atoms/iconButton'
import Popconfirm from 'src/components/atoms/popconfirm'
import Tooltip from 'src/components/atoms/tooltip'
import { Icon } from 'src/components/icons'
import { FullScreenModal } from 'src/components/modals/fullScreenModal'
import useEvents from 'src/hooks/useEvents'

import {
    Action,
    Actions,
    ButtonAction as ButtonActionType,
    ButtonConfirmAction as ButtonConfirmActionType,
    Confirmable,
    ConfirmChildAction,
    FullScreenTriggerAction as FullScreenTriggerActionType,
    IconAction as IconActionType,
    IconConfirmAction as IconConfirmActionType,
    MoreAction as MoreActionType,
} from './types'

import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Space } from 'antd'

const ICON_SIZE = 'sm'
const DROPDOWN_IDENTIFIER = 'action-toolbar-dropdown'

interface Props {
    actions: Actions
    className?: string
}

// Not creating a general "More" icon component, as this should be the only place it's used
const MoreIcon = () => <FontAwesomeIcon size={ICON_SIZE} icon={faEllipsisVertical} />

const ActionToolbar = (props: Props) => {
    const { actions, className } = props

    const sanitizedActions = actions?.filter(Boolean)

    if (!sanitizedActions?.length) {
        return null
    }

    return (
        <Space className={className}>
            {sanitizedActions.map((action, index) => (
                <div key={index}>{renderAction(action)}</div>
            ))}
        </Space>
    )
}

const renderAction = (action: Action) => {
    switch (action.type) {
        case 'icon':
            return <IconAction {...action} />
        case 'button':
            return <ButtonAction {...action} />
        case 'fullscreenTrigger':
            return <FullScreenTriggerAction {...action} />
        case 'more':
            return <MoreAction {...action} />
    }
}

const IconAction = (action: IconActionType | IconConfirmActionType) => {
    const { label, icon, disabled, onClick, tooltip } = action

    const { t } = useTranslation()

    const Icon = icon

    const iconButton = (
        <IconButton
            shape="default"
            disabled={disabled}
            icon={<Icon size={ICON_SIZE} />}
            // Prevents a native tooltip showing up next to the rendered one
            title={tooltip ? undefined : label}
            aria-label={label}
            onClick={hasConfirmProps(action) ? undefined : onClick}
        />
    )

    const actionButton = hasConfirmProps(action) ? (
        <Popconfirm placement="bottom" cancelText={t('Cancel')} {...action.confirmProps}>
            {iconButton}
        </Popconfirm>
    ) : (
        iconButton
    )

    if (tooltip) {
        return <Tooltip title={tooltip}>{actionButton}</Tooltip>
    }

    return actionButton
}

const ButtonAction = (action: ButtonActionType | ButtonConfirmActionType) => {
    const {
        label,
        title,
        variant = 'default',
        disabled,
        danger,
        loading,
        onClick,
        icon,
        iconPosition = 'start',
        tooltip,
    } = action
    const Icon = icon
    const iconElement = icon ? <Icon size={ICON_SIZE} /> : null
    const { t } = useTranslation()

    // A disabled button swallows pointer events, which would keep its tooltip from ever opening
    const buttonStyle = disabled && tooltip ? { pointerEvents: 'none' as const } : undefined

    if (hasConfirmProps(action)) {
        const { confirmProps } = action

        const actionButton = (
            <Popconfirm
                placement="bottom"
                cancelText={t('Cancel')}
                {...confirmProps}
                onConfirm={(e) => {
                    confirmProps.onConfirm(e)
                }}
            >
                <Button
                    type={variant}
                    disabled={disabled}
                    title={title ?? label}
                    danger={danger}
                    loading={loading}
                    icon={iconElement}
                    iconPosition={iconPosition}
                    style={buttonStyle}
                >
                    {label}
                </Button>
            </Popconfirm>
        )

        if (tooltip) {
            return <Tooltip title={tooltip}>{actionButton}</Tooltip>
        }

        return actionButton
    }

    const actionButton = (
        <Button
            type={variant}
            disabled={disabled}
            title={title ?? label}
            danger={danger}
            loading={loading}
            onClick={onClick}
            icon={iconElement}
            iconPosition={iconPosition}
            style={buttonStyle}
        >
            {label}
        </Button>
    )

    if (tooltip) {
        return <Tooltip title={tooltip}>{actionButton}</Tooltip>
    }

    return actionButton
}

const FullScreenTriggerAction = (action: FullScreenTriggerActionType) => {
    const { label, disabled, fullScreenModalProps } = action
    const { title, content, cancelText, okText } = fullScreenModalProps

    const { t } = useTranslation()

    const [open, setOpen] = useState(false)

    return (
        <>
            <Button disabled={disabled} title={label} onClick={() => setOpen(true)}>
                {label}
            </Button>
            <FullScreenModal
                hideCloseButton
                open={open}
                title={title}
                onClose={() => setOpen(false)}
                actions={[
                    { type: 'button', label: cancelText ?? t('Cancel'), onClick: () => setOpen(false) },
                    { type: 'button', variant: 'primary', label: okText ?? t('Save'), onClick: () => setOpen(false) },
                ]}
            >
                {content}
            </FullScreenModal>
        </>
    )
}

const MoreAction = (action: MoreActionType) => {
    const { label, disabled, buttonType = 'text', children } = action

    const [open, setOpen] = useState(false)

    useEvents(
        document.body,
        {
            click: (event) => {
                if (open && event.target instanceof HTMLElement) {
                    const insideMenu = event.target.closest(`.${DROPDOWN_IDENTIFIER}`)
                    if (!insideMenu) {
                        setOpen(false)
                    }
                }
            },
        },
        open,
    )

    const menuItems = children.map((child, index) => {
        const baseMenuItem = { key: index.toString(), disabled: child.disabled }

        if (hasConfirmProps(child)) {
            const { confirmProps } = child

            const handleConfirm = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                confirmProps.onConfirm(e)
                setOpen(false)
            }

            return {
                ...baseMenuItem,
                label: <ConfirmMenuItem {...child} confirmProps={{ ...confirmProps, onConfirm: handleConfirm }} />,
            }
        } else {
            const { label, icon, onClick } = child

            const handleClick = () => {
                onClick()
                setOpen(false)
            }

            return {
                ...baseMenuItem,
                label: icon ? <SimpleMenuItem label={label} icon={icon} onClick={handleClick} /> : label,
                onClick: handleClick,
            }
        }
    })

    return (
        <Dropdown
            open={open}
            disabled={disabled}
            menu={{ items: menuItems }}
            placement="bottomRight"
            overlayClassName={DROPDOWN_IDENTIFIER}
        >
            <IconButton
                className="text-black-500"
                title={label}
                icon={<MoreIcon />}
                disabled={disabled}
                type={buttonType}
                onClick={(e) => {
                    // Prevent the dropdown from closing immediately
                    e.stopPropagation()
                    e.preventDefault()

                    setOpen((prev) => !prev)
                }}
            />
        </Dropdown>
    )
}

const ConfirmMenuItem = (props: ConfirmChildAction) => {
    const { label, icon, disabled, confirmProps } = props

    const { t } = useTranslation()

    const Icon = icon

    return (
        <Popconfirm placement="bottom" {...confirmProps} cancelText={t('Cancel')}>
            <Button
                disabled={disabled}
                className="h-auto p-0 hover:!bg-transparent"
                type="text"
                icon={<Icon fixedWidth style={{ fontSize: '1.25em' /* Default size is too small */ }} />}
            >
                {label}
            </Button>
        </Popconfirm>
    )
}

const SimpleMenuItem = ({ label, icon, onClick }: { label: string; icon: Icon; onClick: () => void }) => {
    const Icon = icon

    return (
        <Button
            className="h-auto p-0 hover:!bg-transparent"
            type="text"
            icon={<Icon fixedWidth style={{ fontSize: '1.25em' }} />}
            onClick={(e) => {
                e.stopPropagation()
                onClick()
            }}
        >
            {label}
        </Button>
    )
}

const hasConfirmProps = <T,>(action: T): action is T & Confirmable => {
    return action.hasOwnProperty('confirmProps')
}

export default ActionToolbar
